// ── Charger le planning Excel (fichier fait main) ──────────────────
// 1. choisir le fichier  2. choisir les onglets / la période + relier les noms
// 3. vérifier l'aperçu  4. Valider. Rien n'est écrit avant « Valider ».
import { useEffect, useMemo, useState } from 'react'
import { X, FileSpreadsheet, AlertTriangle, CheckCircle, Upload } from 'lucide-react'
import { format, startOfWeek, addDays } from 'date-fns'
import api from '../lib/api'

interface Sheet { name: string; weeks: number; dateFrom: string | null; dateTo: string | null; lines: number }
interface Person {
  nomExcel: string; employeeId: string | null; employeeName: string | null
  source: 'fiche' | 'suggestion' | 'aucune' | 'choisi' | 'ignoré' | 'archivé'
  color: string | null
  jours: number; heuresParSemaine: Record<string, number>
}
interface Slot { start: string; end: string }
interface Preview {
  fileName: string; sheets: Sheet[]; dateFrom: string | null; dateTo: string | null; days: number
  lines: number; people: Person[]; unknown: string[]
  conflicts: { date: string; nomExcel: string; sheets: string[] }[]
  anomalies: { sheet: string; row: number; date: string; nomExcel: string; message: string }[]
  existingShiftsToReplace: number
  employees: { id: string; name: string; nomPlanning: string | null }[]
  detail: { date: string; nomExcel: string; matin: Slot | null; aprem: Slot | null; note: string | null }[]
}

const iso = (d: Date) => format(d, 'yyyy-MM-dd')
const frDate = (s: string) => new Date(s + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })
const overlaps = (s: Sheet, from: string, to: string) => !!s.dateFrom && !!s.dateTo && s.dateFrom <= to && s.dateTo >= from

// Onglets cochés par défaut : le moins d'onglets possible pour couvrir la période
// (un onglet qui couvre tout suffit ; évite les doublons entre onglets qui bloquent la validation)
function defaultSheets(list: Sheet[], from: string, to: string): string[] {
  const days = (s: Sheet) => {
    const a = s.dateFrom! > from ? s.dateFrom! : from
    const b = s.dateTo! < to ? s.dateTo! : to
    return Math.max(0, (new Date(b).getTime() - new Date(a).getTime()) / 86400000 + 1)
  }
  const cands = list.filter(s => overlaps(s, from, to)).sort((x, y) => days(y) - days(x) || (y.dateTo! > x.dateTo! ? 1 : -1))
  const chosen: Sheet[] = []
  let coveredFrom = '', coveredTo = ''
  for (const s of cands) {
    const a = s.dateFrom! > from ? s.dateFrom! : from
    const b = s.dateTo! < to ? s.dateTo! : to
    if (chosen.length && a >= coveredFrom && b <= coveredTo) continue // déjà couvert
    chosen.push(s)
    coveredFrom = !coveredFrom || a < coveredFrom ? a : coveredFrom
    coveredTo = !coveredTo || b > coveredTo ? b : coveredTo
    if (coveredFrom <= from && coveredTo >= to) break
  }
  return chosen.map(s => s.name)
}

export default function ImportPlanningExcel({ establishmentId, onClose, onImported }: {
  establishmentId: string; onClose: () => void; onImported: () => void
}) {
  const monday = startOfWeek(new Date(), { weekStartsOn: 1 })
  const [file, setFile] = useState<File | null>(null)
  const [dateFrom, setDateFrom] = useState(iso(monday))
  const [dateTo, setDateTo] = useState(iso(addDays(monday, 5 * 7 - 1)))
  const [allSheets, setAllSheets] = useState<Sheet[]>([])
  const [sheets, setSheets] = useState<string[]>([])
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [preview, setPreview] = useState<Preview | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState<{ importId: string; shiftsCreated: number; shiftsCancelled: number } | null>(null)
  const [showDetail, setShowDetail] = useState(false)

  function form(extra: Record<string, any> = {}) {
    const fd = new FormData()
    fd.append('file', file!)
    fd.append('establishmentId', establishmentId)
    fd.append('dateFrom', dateFrom)
    fd.append('dateTo', dateTo)
    fd.append('sheets', JSON.stringify(sheets))
    fd.append('mapping', JSON.stringify(mapping))
    for (const [k, v] of Object.entries(extra)) fd.append(k, typeof v === 'string' ? v : JSON.stringify(v))
    return fd
  }

  // Premier passage : lister les onglets et pré-cocher ceux qui touchent la période
  useEffect(() => {
    if (!file) return
    setLoading(true); setError(''); setPreview(null)
    const fd = new FormData()
    fd.append('file', file)
    fd.append('establishmentId', establishmentId)
    api.post('/planning-import/preview', fd)
      .then(r => {
        const list: Sheet[] = r.data.sheets
        setAllSheets(list)
        setSheets(defaultSheets(list, dateFrom, dateTo))
      })
      .catch(e => setError(e.response?.data?.error || 'Lecture du fichier impossible'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file])

  // Aperçu recalculé à chaque changement d'onglets, de période ou de correspondance
  useEffect(() => {
    if (!file || sheets.length === 0) { setPreview(null); return }
    const t = setTimeout(() => {
      setLoading(true); setError('')
      api.post('/planning-import/preview', form())
        .then(r => setPreview(r.data))
        .catch(e => setError(e.response?.data?.error || 'Aperçu impossible'))
        .finally(() => setLoading(false))
    }, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, sheets, dateFrom, dateTo, mapping])

  const weeks = useMemo(() => {
    const s = new Set<string>()
    preview?.people.forEach(p => Object.keys(p.heuresParSemaine).forEach(w => s.add(w)))
    return [...s].sort()
  }, [preview])

  const blocking = !preview || preview.unknown.length > 0 || preview.conflicts.length > 0 || preview.lines === 0

  async function commit() {
    if (!preview || blocking) return
    if (preview.existingShiftsToReplace > 0 &&
      !confirm(`${preview.existingShiftsToReplace} horaire(s) déjà dans l'appli sur ces jours seront remplacés (gardés en « annulé », récupérables). Continuer ?`)) return
    setSaving(true); setError('')
    try {
      const r = await api.post('/planning-import/commit', form())
      setDone(r.data)
      onImported()
    } catch (e: any) {
      setError(e.response?.data?.error || 'Import refusé')
    } finally {
      setSaving(false)
    }
  }

  async function undo() {
    if (!done || !confirm('Annuler cet import et remettre les horaires d\'avant ?')) return
    setSaving(true)
    try {
      await api.post(`/planning-import/${done.importId}/undo`)
      setDone(null)
      onImported()
      onClose()
    } catch (e: any) {
      setError(e.response?.data?.error || 'Annulation impossible')
    } finally {
      setSaving(false)
    }
  }

  const people = preview?.people ?? []

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="text-green-600" size={20} />
            <h2 className="text-lg font-semibold text-gray-900">Charger le planning Excel</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-5">
          {done ? (
            <div className="text-center py-8 space-y-3">
              <CheckCircle className="mx-auto text-green-500" size={44} />
              <p className="text-lg font-semibold text-gray-900">Planning chargé</p>
              <p className="text-sm text-gray-600">
                {done.shiftsCreated} horaires créés
                {done.shiftsCancelled > 0 && ` · ${done.shiftsCancelled} anciens horaires mis de côté`}
              </p>
              <p className="text-xs text-gray-400">Les employés voient leurs horaires dans l'appli, et les tâches du jour sont réparties sur les présents.</p>
              <button onClick={undo} disabled={saving} className="text-sm text-red-600 hover:text-red-700 underline disabled:opacity-50">
                Annuler cet import
              </button>
            </div>
          ) : <>
            {/* 1. Fichier */}
            <section>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">1. Fichier</h3>
              <label className="flex items-center gap-3 border-2 border-dashed border-gray-200 rounded-xl p-4 cursor-pointer hover:border-green-400">
                <Upload size={18} className="text-gray-400" />
                <span className="text-sm text-gray-600">{file ? file.name : 'Choisir le fichier planning (.xlsm ou .xlsx)'}</span>
                <input type="file" accept=".xlsm,.xlsx" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) { setFile(f); setMapping({}) } }} />
              </label>
            </section>

            {/* 2. Période + onglets */}
            {allSheets.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">2. Période et onglets</h3>
                <div className="flex flex-wrap items-center gap-3 mb-3 text-sm">
                  <span className="text-gray-500">Du</span>
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="border rounded-lg px-2 py-1" />
                  <span className="text-gray-500">au</span>
                  <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="border rounded-lg px-2 py-1" />
                  <button onClick={() => setSheets(defaultSheets(allSheets, dateFrom, dateTo))}
                    className="text-xs text-blue-600 hover:underline">Cocher les onglets de cette période</button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {allSheets.map(s => {
                    const on = sheets.includes(s.name)
                    const inPeriod = overlaps(s, dateFrom, dateTo)
                    return (
                      <button key={s.name}
                        onClick={() => setSheets(on ? sheets.filter(x => x !== s.name) : [...sheets, s.name])}
                        className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                          on ? 'bg-green-50 border-green-400 text-green-800' : inPeriod ? 'border-gray-300 text-gray-700' : 'border-gray-100 text-gray-300'
                        }`}>
                        {s.name}
                        {s.dateFrom && <span className="ml-1 opacity-70">({frDate(s.dateFrom)} → {frDate(s.dateTo!)})</span>}
                      </button>
                    )
                  })}
                </div>
              </section>
            )}

            {loading && <p className="text-sm text-gray-400">Lecture…</p>}
            {error && <p className="text-sm text-red-600 flex items-center gap-2"><AlertTriangle size={14} /> {error}</p>}

            {preview && <>
              {/* Alertes bloquantes */}
              {preview.conflicts.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
                  <p className="font-semibold mb-1">Deux onglets donnent des horaires différents pour les mêmes jours — décochez l'un des deux :</p>
                  {preview.conflicts.slice(0, 6).map((c, i) => (
                    <p key={i} className="text-xs">{frDate(c.date)} · {c.nomExcel} · {c.sheets.join(' / ')}</p>
                  ))}
                </div>
              )}
              {preview.anomalies.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                  <p className="font-semibold mb-1">{preview.anomalies.length} case(s) horaire(s) illisible(s), ignorée(s) :</p>
                  {preview.anomalies.slice(0, 6).map((a, i) => (
                    <p key={i} className="text-xs">{a.sheet} ligne {a.row} · {frDate(a.date)} · {a.nomExcel} : {a.message}</p>
                  ))}
                </div>
              )}

              {/* 3. Correspondance des noms */}
              <section>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  3. Qui est qui ({preview.days} jours, {preview.dateFrom && frDate(preview.dateFrom)} → {preview.dateTo && frDate(preview.dateTo)})
                </h3>
                <div className="border border-gray-200 rounded-xl overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs text-gray-500">
                      <tr>
                        <th className="text-left px-3 py-2">Nom dans l'Excel</th>
                        <th className="text-left px-3 py-2">Fiche employé</th>
                        {weeks.map(w => <th key={w} className="px-2 py-2 text-right">{w.slice(5)}</th>)}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {people.map(p => {
                        const archived = p.source === 'archivé' && !mapping[p.nomExcel]
                        const value = archived ? 'ignore' : (mapping[p.nomExcel] ?? p.employeeId ?? '')
                        const warn = !p.employeeId && p.source !== 'ignoré' && !archived
                        return (
                          <tr key={p.nomExcel} className={warn ? 'bg-amber-50' : ''}>
                            <td className="px-3 py-2 font-medium text-gray-800">
                              <span className="inline-flex items-center gap-2">
                                <span className="w-3.5 h-3.5 rounded border border-gray-300 flex-shrink-0" style={{ backgroundColor: p.color || 'transparent' }} title={p.color ? `Couleur Excel ${p.color}` : 'Pas de couleur dans l’Excel'} />
                                {p.nomExcel}
                              </span>
                            </td>
                            <td className="px-3 py-2 min-w-[160px]">
                              <select value={mapping[p.nomExcel] === 'ignore' ? 'ignore' : value}
                                onChange={e => setMapping(m => ({ ...m, [p.nomExcel]: e.target.value }))}
                                className={`text-sm border rounded-lg px-2 py-1 w-full ${warn ? 'border-amber-400' : 'border-gray-200'}`}>
                                <option value="">— À relier —</option>
                                {preview.employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                                <option value="ignore">Ignorer cette ligne</option>
                              </select>
                              {archived && (
                                <p className="text-[11px] text-gray-500 mt-0.5">Employé archivé : ses lignes sont ignorées</p>
                              )}
                              {p.source === 'suggestion' && !mapping[p.nomExcel] && (
                                <p className="text-[11px] text-blue-600 mt-0.5">Proposé automatiquement — vérifiez</p>
                              )}
                            </td>
                            {weeks.map(w => (
                              <td key={w} className="px-2 py-2 text-right text-gray-600 tabular-nums">
                                {p.heuresParSemaine[w] ? `${p.heuresParSemaine[w].toFixed(1)} h` : '—'}
                              </td>
                            ))}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-400 mt-1">Le lien et la couleur de l’Excel sont retenus sur la fiche : au prochain chargement, plus rien à relier.</p>
              </section>

              {/* Détail jour par jour */}
              <section>
                <button onClick={() => setShowDetail(v => !v)} className="text-sm text-blue-600 hover:underline">
                  {showDetail ? 'Masquer' : 'Voir'} le détail jour par jour
                </button>
                {showDetail && (
                  <div className="mt-2 max-h-72 overflow-y-auto border border-gray-100 rounded-xl text-xs">
                    {[...new Set(preview.detail.map(d => d.date))].map(date => (
                      <div key={date} className="px-3 py-2 border-b border-gray-50">
                        <p className="font-semibold text-gray-700 mb-1">{frDate(date)}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-gray-600">
                          {preview.detail.filter(d => d.date === date).map(d => (
                            <span key={d.nomExcel}>
                              <b>{d.nomExcel}</b> {d.matin && `${d.matin.start}-${d.matin.end}`}{d.matin && d.aprem && ' / '}{d.aprem && `${d.aprem.start}-${d.aprem.end}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {preview.existingShiftsToReplace > 0 && (
                <p className="text-xs text-gray-500">
                  {preview.existingShiftsToReplace} horaire(s) déjà dans l'appli sur ces jours seront remplacés (mis de côté, pas effacés).
                </p>
              )}
            </>}
          </>}
        </div>

        {!done && (
          <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-gray-100 flex-wrap">
            <p className="text-xs text-gray-500">
              {preview && preview.unknown.length > 0 && `${preview.unknown.length} nom(s) à relier avant de valider`}
            </p>
            <div className="flex gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Fermer</button>
              <button onClick={commit} disabled={blocking || saving || loading}
                className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg disabled:opacity-40">
                {saving ? 'Enregistrement…' : 'Valider le planning'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
